/**
 * Generic MUI `Autocomplete` multi-select keyed by entity UUID (streams, teams, etc.).
 *
 * Where: `LeadsFilterBar`, `MessageList` filters, and other multi-entity pickers.
 * Uses: dedupes options by uuid; optional disambiguation when labels collide.
 * Behavior: controlled via `valueUuids` + `onChange`; supports custom tag rendering for dense toolbars.
 */
import React from 'react';
import { Autocomplete, TextField, createFilterOptions } from '@mui/material';

export interface EntityOption {
  uuid: string;
  name?: string | null;
}

export interface EntityMultiSelectProps<T extends EntityOption = EntityOption> {
  options: T[];
  valueUuids: string[];
  onChange: (uuids: string[]) => void;
  placeholder?: string;
  size?: 'small' | 'medium';
  // Optional custom label extractor
  getLabel?: (option: T) => string;
  // Whether to show a short uuid suffix to disambiguate duplicate labels
  showDisambiguator?: boolean;
  // MUI customization hooks for page-specific UI needs (e.g., Leads)
  label?: string;
  renderTags?: (value: T[], getTagProps: (params: { index: number }) => any) => React.ReactNode;
  autocompleteSx?: any;
  textFieldSx?: any;
}

// Core label function: use name, fallback to empty string
function defaultGetLabel(option: EntityOption): string {
  return (option.name || '').trim();
}

// Deduplicate options by uuid and provide deterministic filtering even when labels are duplicate
function dedupeByUuid<T extends EntityOption>(items: T[]): T[] {
  const map = new Map<string, T>();
  for (const item of items) {
    if (!item || !item.uuid) continue;
    if (!map.has(item.uuid)) map.set(item.uuid, item);
  }
  return Array.from(map.values());
}

const labelFilter = createFilterOptions<EntityOption>({
  stringify: (option) => `${(option.name || '').toLowerCase()}`,
  trim: true,
});

/**
 * EntityMultiSelect: Duplicate-safe multi-select for entities (projects/streams ...)
 * - Stable identity by uuid
 * - Filtering by name
 * - Renders duplicate labels, but selection state is per-uuid to prevent flicker
 */
export const EntityMultiSelect = <T extends EntityOption = EntityOption>({
  options,
  valueUuids,
  onChange,
  placeholder = 'Select items',
  size = 'small',
  getLabel,
  showDisambiguator = false,
  label,
  renderTags,
  autocompleteSx,
  textFieldSx,
}: EntityMultiSelectProps<T>) => {
  const safeOptions = React.useMemo(() => dedupeByUuid(options), [options]);
  const computeLabel = React.useCallback((o: T) => (getLabel ? getLabel(o) : defaultGetLabel(o)), [getLabel]);

  // Selection is driven by uuid; map current uuids to option objects if present
  const selectedOptions = React.useMemo(() => {
    const byId = new Map(safeOptions.map((o) => [o.uuid, o]));
    return valueUuids.map((id) => byId.get(id)).filter(Boolean) as T[];
  }, [safeOptions, valueUuids]);

  return (
    <Autocomplete
      multiple
      size={size}
      options={safeOptions}
      getOptionLabel={(option) => computeLabel(option as T)}
      isOptionEqualToValue={(option, value) => option.uuid === value.uuid}
      value={selectedOptions}
      renderTags={renderTags as any}
      onChange={(_, newValue) => {
        const uuids = newValue.map((v) => v.uuid);
        onChange(uuids);
      }}
      filterOptions={(opts, state) => {
        const filtered = labelFilter(opts as unknown as EntityOption[], state as any);
        // Preserve duplicates intentionally; do not merge by label, identity is uuid
        return filtered as unknown as T[];
      }}
      renderInput={(params) => (
        <TextField {...params} placeholder={placeholder} size={size} label={label} sx={textFieldSx} />
      )}
      renderOption={({ key, ...props }, option) => {
        const label = computeLabel(option as T);
        // Disambiguate duplicates with short uuid suffix
        const disambiguator = option.uuid?.slice(4, 8);
        return (
          <li key={option.uuid ?? key} {...props}>
            {label || '(untitled)'}
            {showDisambiguator && (
              <>
                {label ? ' ' : ''}
                <span style={{ color: 'var(--mui-palette-text-secondary)', fontSize: 12 }}>• {disambiguator}</span>
              </>
            )}
          </li>
        );
      }}
      filterSelectedOptions
      autoHighlight
      sx={autocompleteSx}
    />
  );
};

export default EntityMultiSelect;


